import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { db } from '../firebaseConfig';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  startAfter, 
  limit, 
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  getDoc,
  Timestamp 
} from 'firebase/firestore';
import { FaCheck, FaSpinner, FaTrash, FaAngleLeft, FaAngleRight } from 'react-icons/fa';

// Styled Components
const Container = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  font-size: 1.8rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 1.5rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -8px;
    width: 50px;
    height: 3px;
    background-color: #546140;
  }
`;

const OrdersTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  overflow: hidden;
  background-color: white;
`;

const TableHead = styled.thead`
  background-color: #546140;
  color: white;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #e9ecef;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background-color: ${props => props.clickable ? '#f8f9fa' : 'transparent'};
  }
`;

const TableHeader = styled.th`
  padding: 16px;
  text-align: left;
  font-weight: 600;
`;

const TableCell = styled.td`
  padding: 16px;
  text-align: left;
  color: #333;
  vertical-align: top;
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  gap: 8px;
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const DeliveredButton = styled(Button)`
  background-color: ${props => props.delivered ? '#e9ecef' : '#546140'};
  color: ${props => props.delivered ? '#6c757d' : 'white'};
  
  &:hover:not(:disabled) {
    background-color: ${props => props.delivered ? '#e9ecef' : '#3e472f'};
  }
`;

const DeleteButton = styled(Button)`
  background-color: #dc3545;
  color: white;
  
  &:hover:not(:disabled) {
    background-color: #bd2130;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const EmptyStateIcon = styled.div`
  font-size: 3rem;
  color: #d1d5db;
  margin-bottom: 1rem;
`;

const EmptyStateText = styled.p`
  font-size: 1.1rem;
  color: #6b7280;
  margin-bottom: 1.5rem;
`;

const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const SpinnerIcon = styled(FaSpinner)`
  font-size: 2rem;
  color: #546140;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const Badge = styled.span`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 16px;
  font-size: 0.75rem;
  font-weight: 600;
  background-color: ${props => {
    switch (props.status) {
      case 'delivered': return '#d1fae5';
      case 'confirmed': return '#fffbeb';
      case 'shipped': return '#dbeafe';
      default: return '#f3f4f6';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'delivered': return '#047857';
      case 'confirmed': return '#d97706';
      case 'shipped': return '#1e40af';
      default: return '#6b7280';
    }
  }};
`;

const ItemsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const ItemRow = styled.li`
  display: flex;
  justify-content: space-between;
  font-size: 0.85rem;
  padding: 4px 0;
  
  &:not(:last-child) {
    border-bottom: 1px dashed #e5e7eb;
    padding-bottom: 4px;
    margin-bottom: 4px;
  }
`;

const ItemName = styled.span`
  flex: 1;
`;

const ItemQuantity = styled.span`
  color: #6b7280;
  font-size: 0.75rem;
  margin-left: 8px;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 2rem;
  gap: 1rem;
`;

const PageIndicator = styled.span`
  color: #6b7280;
`;

const PaginationButton = styled(Button)`
  background-color: #f9fafb;
  color: #111827;
  border: 1px solid #d1d5db;
  
  &:hover:not(:disabled) {
    background-color: #f3f4f6;
  }
  
  &:disabled {
    background-color: #f3f4f6;
    color: #9ca3af;
    cursor: not-allowed;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  padding: 2rem;
  border-radius: 8px;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #333;
`;

const ModalText = styled.p`
  margin-bottom: 1.5rem;
  color: #4b5563;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
`;

const CancelButton = styled(Button)`
  background-color: #f3f4f6;
  color: #4b5563;
  
  &:hover {
    background-color: #e5e7eb;
  }
`;

const ConfirmButton = styled(DeleteButton)``;

const ConfirmedOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [actionLoading, setActionLoading] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  
  const ordersPerPage = 5;
  
  const fetchOrders = useCallback(async (reset = true) => {
    try {
      setLoading(true);
      
      let ordersQuery = query(
        collection(db, 'orders'),
        where('status', '==', 'confirmed'),
        orderBy('createdAt', 'desc'),
        limit(ordersPerPage)
      );
      
      if (!reset && lastVisible) {
        ordersQuery = query(
          collection(db, 'orders'),
          where('status', '==', 'confirmed'),
          orderBy('createdAt', 'desc'),
          startAfter(lastVisible),
          limit(ordersPerPage)
        );
      }
      
      const querySnapshot = await getDocs(ordersQuery);
      
      if (querySnapshot.empty) {
        setHasMore(false);
        setLoading(false);
        
        if (reset) {
          setOrders([]);
        }
        return;
      }
      
      const lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1];
      setLastVisible(lastDoc);
      
      const fetchedOrders = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date()
      }));
      
      setOrders(reset ? fetchedOrders : prevOrders => [...prevOrders, ...fetchedOrders]);
      setHasMore(querySnapshot.docs.length === ordersPerPage);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  }, [lastVisible, ordersPerPage]);
  
  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);
  
  const nextPage = () => {
    if (hasMore) {
      fetchOrders(false);
      setCurrentPage(currentPage + 1);
    }
  };
  
  const prevPage = () => {
    if (currentPage > 1) {
      // We need to refetch from the beginning and then paginate to the previous page
      setCurrentPage(currentPage - 1);
      // This is a workaround as Firestore doesn't support "backward" pagination directly
      fetchOrdersFromBeginning(currentPage - 1);
    }
  };
  
  const fetchOrdersFromBeginning = async (targetPage) => {
    try {
      setLoading(true);
      
      let tempLastVisible = null;
      let page = 1;
      
      const ordersQuery = query(
        collection(db, 'orders'),
        where('status', '==', 'confirmed'),
        orderBy('createdAt', 'desc'),
        limit(ordersPerPage)
      );
      
      let querySnapshot = await getDocs(ordersQuery);
      
      // If there are no results at all, we're done
      if (querySnapshot.empty) {
        setOrders([]);
        setHasMore(false);
        setLoading(false);
        return;
      }
      
      tempLastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];
      
      // If we want the first page, we're already done
      if (targetPage === 1) {
        const fetchedOrders = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date()
        }));
        
        setOrders(fetchedOrders);
        setLastVisible(tempLastVisible);
        setHasMore(querySnapshot.docs.length === ordersPerPage);
        setLoading(false);
        return;
      }
      
      // Otherwise, we need to keep paginating until we reach the target page
      while (page < targetPage) {
        const nextQuery = query(
          collection(db, 'orders'),
          where('status', '==', 'confirmed'),
          orderBy('createdAt', 'desc'),
          startAfter(tempLastVisible),
          limit(ordersPerPage)
        );
        
        querySnapshot = await getDocs(nextQuery);
        
        // If we've run out of results, we can't go to the requested page
        if (querySnapshot.empty) {
          // We'll stay at the last possible page
          break;
        }
        
        tempLastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];
        page++;
      }
      
      // Now we've reached the target page (or the last possible page)
      const fetchedOrders = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date()
      }));
      
      setOrders(fetchedOrders);
      setLastVisible(tempLastVisible);
      setHasMore(querySnapshot.docs.length === ordersPerPage);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleMarkAsDelivered = async (orderId) => {
    try {
      setActionLoading(orderId);
      
      const orderRef = doc(db, 'orders', orderId);
      const orderSnap = await getDoc(orderRef);
      
      if (!orderSnap.exists()) {
        alert('Order not found');
        return;
      }
      
      await updateDoc(orderRef, {
        status: 'delivered',
        deliveryStatus: 'delivered',
        deliveredAt: Timestamp.fromDate(new Date()),
        updatedAt: Timestamp.fromDate(new Date())
      });
      
      // Update local state
      setOrders(orders.map(order => 
        order.id === orderId 
          ? { 
              ...order, 
              status: 'delivered', 
              deliveryStatus: 'delivered',
              deliveredAt: new Date(),
              updatedAt: new Date()
            } 
          : order
      ));
      
    } catch (error) {
      console.error('Error marking order as delivered:', error);
      alert('Failed to mark order as delivered');
    } finally {
      setActionLoading(null);
    }
  };
  
  const confirmDeleteOrder = (order) => {
    setOrderToDelete(order);
    setShowDeleteModal(true);
  };
  
  const handleDeleteOrder = async () => {
    if (!orderToDelete) return;
    
    try {
      setActionLoading(orderToDelete.id);
      
      const orderRef = doc(db, 'orders', orderToDelete.id);
      await deleteDoc(orderRef);
      
      // Update local state
      setOrders(orders.filter(order => order.id !== orderToDelete.id));
      
      setShowDeleteModal(false);
      setOrderToDelete(null);
    } catch (error) {
      console.error('Error deleting order:', error);
      alert('Failed to delete order');
    } finally {
      setActionLoading(null);
    }
  };
  
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (loading && orders.length === 0) {
    return (
      <Container>
        <PageTitle>Confirmed Orders</PageTitle>
        <LoadingWrapper>
          <SpinnerIcon />
          <p>Loading orders...</p>
        </LoadingWrapper>
      </Container>
    );
  }

  return (
    <Container>
      <PageTitle>Confirmed Orders</PageTitle>
      
      {orders.length === 0 ? (
        <EmptyState>
          <EmptyStateIcon>📦</EmptyStateIcon>
          <EmptyStateText>No confirmed orders found</EmptyStateText>
        </EmptyState>
      ) : (
        <>
          <OrdersTable>
            <TableHead>
              <TableRow>
                <TableHeader>Order ID</TableHeader>
                <TableHeader>Customer</TableHeader>
                <TableHeader>Order Date</TableHeader>
                <TableHeader>Items</TableHeader>
                <TableHeader>Total Amount</TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader>Actions</TableHeader>
              </TableRow>
            </TableHead>
            <tbody>
              {orders.map(order => (
                <TableRow key={order.id}>
                  <TableCell>{order.id.slice(0, 8)}...</TableCell>
                  <TableCell>
                    <div>{order.shippingAddress?.name || order.userName || 'N/A'}</div>
                    <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                      {order.userEmail || order.shippingAddress?.email || 'N/A'}
                    </div>
                  </TableCell>
                  <TableCell>{formatDate(order.createdAt)}</TableCell>
                  <TableCell>
                    <ItemsList>
                      {order.items && order.items.map((item, index) => (
                        <ItemRow key={index}>
                          <ItemName>{item.name}</ItemName>
                          <ItemQuantity>x{item.quantity}</ItemQuantity>
                        </ItemRow>
                      ))}
                    </ItemsList>
                  </TableCell>
                  <TableCell>{formatCurrency(order.total || order.totalAmount || 0)}</TableCell>
                  <TableCell>
                    <Badge status={order.deliveryStatus || order.status || 'confirmed'}>
                      {order.deliveryStatus || order.status || 'Confirmed'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <DeliveredButton 
                        delivered={order.deliveryStatus === 'delivered' || order.status === 'delivered'} 
                        disabled={
                          actionLoading === order.id || 
                          order.deliveryStatus === 'delivered' || 
                          order.status === 'delivered'
                        }
                        onClick={() => handleMarkAsDelivered(order.id)}
                      >
                        {actionLoading === order.id ? (
                          <FaSpinner className="animate-spin" />
                        ) : (
                          <FaCheck />
                        )}
                        {order.deliveryStatus === 'delivered' || order.status === 'delivered' 
                          ? 'Delivered' 
                          : 'Mark Delivered'
                        }
                      </DeliveredButton>
                      
                      <DeleteButton
                        disabled={actionLoading === order.id}
                        onClick={() => confirmDeleteOrder(order)}
                      >
                        {actionLoading === order.id ? (
                          <FaSpinner className="animate-spin" />
                        ) : (
                          <FaTrash />
                        )}
                      </DeleteButton>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </OrdersTable>
          
          <Pagination>
            <PaginationButton 
              onClick={prevPage} 
              disabled={currentPage === 1 || loading}
            >
              <FaAngleLeft /> Previous
            </PaginationButton>
            
            <PageIndicator>Page {currentPage}</PageIndicator>
            
            <PaginationButton 
              onClick={nextPage} 
              disabled={!hasMore || loading}
            >
              Next <FaAngleRight />
            </PaginationButton>
          </Pagination>
        </>
      )}
      
      {showDeleteModal && (
        <Modal>
          <ModalContent>
            <ModalTitle>Confirm Deletion</ModalTitle>
            <ModalText>
              Are you sure you want to delete this order? This action cannot be undone.
            </ModalText>
            <ModalActions>
              <CancelButton onClick={() => setShowDeleteModal(false)}>
                Cancel
              </CancelButton>
              <ConfirmButton 
                onClick={handleDeleteOrder}
                disabled={actionLoading === (orderToDelete?.id || '')}
              >
                {actionLoading === (orderToDelete?.id || '') ? (
                  <FaSpinner className="animate-spin" />
                ) : (
                  <FaTrash />
                )}
                Delete Order
              </ConfirmButton>
            </ModalActions>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

export default ConfirmedOrders; 